import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { realLinkProps } from '../links/realLinkProps';
import { useRouterNavigate } from '../links/useEntityLinkProps';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AddRegular, DeleteRegular } from '@fluentui/react-icons';
import type { CountyAllocationSaveResult, CountyShare } from '../../types';
import {
  useClientTaxRateConfig,
  useObligationCountyAllocation,
  useUpsertClientTaxRateConfig,
  useUpsertObligationCountyAllocation,
} from '../../services/complianceApi';
import { apiErrorMessage } from '../../lib/apiErrorMessage';
import {
  buildAllocationCounties,
  buildClientConfigPayload,
  editPath,
  effectiveFromIso,
  fractionToPercentInput,
  validateRows,
  type EditRow,
} from '../../utils/surtaxEdit';
import { yearInPinnedZone, yearMonthInPinnedZone } from '../../utils/pinnedZone';

/**
 * Edit an obligation's county surtax — spec §5.2 (2026-09-03 per-county allocation design).
 *
 * Two different records sit behind this one dialog, and it must never blur which one a save
 * writes:
 *
 *   • ONE row, no split on file → the CLIENT-level `clientTaxRateConfig`. Exactly what the old
 *     `ClientTaxRateConfigDialog` wrote, and it affects every obligation of the client without a
 *     split of its own — so the dialog lists them.
 *   • A split (Add county), or an existing one → an effective-dated `obligationCountyAllocation`
 *     for THIS obligation only, from the chosen month. Earlier periods keep what governed them.
 *
 * Rates are entered as a PERCENT and stored as a fraction. Open drafts an allocation change
 * affects are FLAGGED, never recomputed ("a silent mass rewrite is the same class of move as the
 * defect being fixed") — and since the flag travels only on the save response, the dialog stays
 * open to show it rather than letting it vanish.
 */

export interface SurtaxEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  /** The id this obligation's FILINGS carry. See `ObligationSurtaxLine` for why it matters. */
  obligationId: string;
  obligationLabel: string;
  /** This client's obligations, listed when a save writes the client-level default. */
  clientObligationLabels: string[];
  /** Injectable clock, so tests can pin "this month". */
  now?: Date;
}

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  county: { flex: '2 1 12rem' },
  rate: { flex: '1 1 9rem' },
  share: { flex: '1 1 8rem' },
  removeRow: { alignSelf: 'flex-end' },
  month: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: tokens.spacingHorizontalS },
  list: { margin: 0, paddingLeft: tokens.spacingHorizontalXL },
  drafts: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: tokens.spacingVerticalXS },
});

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export default function SurtaxEditDialog({
  open,
  onOpenChange,
  clientId,
  obligationId,
  obligationLabel,
  clientObligationLabels,
  now,
}: SurtaxEditDialogProps) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const linkTo = useRouterNavigate();

  const allocQ = useObligationCountyAllocation(clientId, obligationId);
  const cfgQ = useClientTaxRateConfig(clientId);
  const upsertConfig = useUpsertClientTaxRateConfig();
  const upsertAlloc = useUpsertObligationCountyAllocation();

  const [rows, setRows] = useState<EditRow[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [effectiveMonth, setEffectiveMonth] = useState('');
  const [clearing, setClearing] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saved, setSaved] = useState<CountyAllocationSaveResult | null>(null);
  const nextKey = useRef(0);
  const newKey = () => `row-${nextKey.current++}`;

  // Seed ONCE per opening. Re-seeding whenever a query refetches (window focus, the mutation's own
  // invalidation) would silently throw away what the staff member has typed.
  useEffect(() => {
    if (!open) {
      setSeeded(false);
      setClearing(false);
      setAttempted(false);
      setSubmitError(null);
      setSaved(null);
      return;
    }
    if (seeded || !allocQ.data || !cfgQ.data) return;

    const current = allocQ.data.current;
    if (current && current.counties.length > 0) {
      const multi = current.counties.length > 1;
      setRows(current.counties.map((c) => ({
        key: newKey(),
        county: c.county,
        ratePercent: fractionToPercentInput(c.surtaxRateOverride),
        sharePercent: multi ? fractionToPercentInput(c.share) : '',
      })));
    } else {
      setRows([{
        key: newKey(),
        county: cfgQ.data.county ?? '',
        ratePercent: fractionToPercentInput(cfgQ.data.countySurtaxRateOverride),
        sharePercent: '',
      }]);
    }
    setEffectiveMonth(yearMonthInPinnedZone(now));
    setSeeded(true);
    // `now` is a test seam; a new Date per render must not re-seed, and `seeded` guards that.
  }, [open, seeded, allocQ.data, cfgQ.data, now]);

  const hasExistingSplit = !!allocQ.data?.current && allocQ.data.current.counties.length > 0;
  const path = editPath(hasExistingSplit, rows.length);
  const validation = validateRows(rows, path);
  const monthOk = MONTH_RE.test(effectiveMonth);
  const pending = upsertConfig.isPending || upsertAlloc.isPending;
  const loadError = allocQ.error ?? cfgQ.error;

  const monthLabel = (() => {
    if (!monthOk) return effectiveMonth;
    try {
      return new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric', timeZone: 'UTC' })
        .format(new Date(effectiveFromIso(effectiveMonth)));
    } catch {
      return effectiveMonth;
    }
  })();

  function updateRow(key: string, patch: Partial<EditRow>) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function addCounty() {
    setRows((prev) => {
      // Going from one row to two: the first row's implicit 100% becomes visible as 100, and the
      // new row starts BLANK. Pre-filling 50/50 would be a number nobody entered — the sum check
      // makes the staff member state the split instead.
      const shown = prev.length === 1 && prev[0].sharePercent.trim() === ''
        ? [{ ...prev[0], sharePercent: '100' }]
        : prev;
      return [...shown, { key: newKey(), county: '', ratePercent: '', sharePercent: '' }];
    });
  }

  function removeRow(key: string) {
    setRows((prev) => prev.filter((r) => r.key !== key));
  }

  function fail(err: unknown) {
    setSubmitError(apiErrorMessage(err, t, 'surtaxEdit.saveFailed'));
  }

  function saveAllocation(counties: CountyShare[]) {
    upsertAlloc.mutate(
      { clientId, obligationId, payload: { effectiveFrom: effectiveFromIso(effectiveMonth), counties } },
      {
        onSuccess: (result) => {
          if (result.affectedOpenDrafts && result.affectedOpenDrafts.length > 0) setSaved(result);
          else onOpenChange(false);
        },
        onError: fail,
      },
    );
  }

  function handleSave() {
    setAttempted(true);
    setSubmitError(null);

    if (clearing) {
      if (!monthOk) return;
      saveAllocation([]);
      return;
    }
    if (!validation.ok || rows.length === 0) return;
    if (path === 'allocation' && !monthOk) return;

    if (path === 'client') {
      upsertConfig.mutate(
        { clientId, payload: buildClientConfigPayload(clientId, cfgQ.data, rows[0]) },
        { onSuccess: () => onOpenChange(false), onError: fail },
      );
      return;
    }
    saveAllocation(buildAllocationCounties(rows));
  }

  const showErr = <K extends string>(code: K | undefined) =>
    attempted && code ? t(`surtaxEdit.validation.${code}`) : undefined;

  const multi = rows.length > 1;

  function renderStatement() {
    if (clearing) {
      return (
        <MessageBar intent="warning" data-testid="surtax-writes" data-path="clear">
          <MessageBarBody>
            <MessageBarTitle>{t('surtaxEdit.clearSplitTitle', { obligation: obligationLabel })}</MessageBarTitle>
            {t('surtaxEdit.clearSplitBody', { month: monthLabel })}
          </MessageBarBody>
        </MessageBar>
      );
    }
    if (path === 'client') {
      return (
        <MessageBar intent="warning" data-testid="surtax-writes" data-path="client">
          <MessageBarBody>
            <MessageBarTitle>{t('surtaxEdit.writesClientTitle')}</MessageBarTitle>
            {t('surtaxEdit.writesClientBody')}
            {clientObligationLabels.length > 0 && (
              <>
                <div>{t('surtaxEdit.writesClientList', { count: clientObligationLabels.length })}</div>
                <ul className={styles.list}>
                  {clientObligationLabels.map((label, i) => <li key={`${label}-${i}`}>{label}</li>)}
                </ul>
              </>
            )}
          </MessageBarBody>
        </MessageBar>
      );
    }
    return (
      <MessageBar intent="info" data-testid="surtax-writes" data-path="allocation">
        <MessageBarBody>
          <MessageBarTitle>{t('surtaxEdit.writesAllocationTitle', { obligation: obligationLabel })}</MessageBarTitle>
          {t('surtaxEdit.writesAllocationBody', { month: monthLabel })}{' '}
          {multi ? t('surtaxEdit.allocationIsEstimate') : null}
        </MessageBarBody>
      </MessageBar>
    );
  }

  function renderSaved(result: CountyAllocationSaveResult) {
    return (
      <div className={styles.form}>
        <MessageBar intent="warning" data-testid="surtax-affected-drafts">
          <MessageBarBody>
            <MessageBarTitle>
              {t('surtaxEdit.affectedDraftsTitle', { count: result.affectedOpenDrafts.length })}
            </MessageBarTitle>
            {t('surtaxEdit.affectedDraftsBody')}
          </MessageBarBody>
        </MessageBar>
        <div className={styles.drafts}>
          {result.affectedOpenDrafts.map((d) => (
            <Button
              key={d.taskId}
              as="a"
              appearance="transparent"
              {...realLinkProps(`/compliance/tasks/${d.taskId}?clientId=${encodeURIComponent(clientId)}`, {
                navigate: linkTo,
                onFollow: () => onOpenChange(false),
              })}
            >
              {t('surtaxEdit.openDraft', { period: d.periodLabel })}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  function renderForm() {
    if (loadError) {
      return (
        <MessageBar intent="error">
          <MessageBarBody>{apiErrorMessage(loadError, t, 'surtaxEdit.loadError')}</MessageBarBody>
        </MessageBar>
      );
    }
    if (!seeded) return <Spinner label={t('surtaxEdit.loading')} />;

    return (
      <div className={styles.form}>
        {renderStatement()}

        {!clearing && rows.map((r) => {
          const e = validation.rowErrors[r.key] ?? {};
          return (
            <div key={r.key} className={styles.row} data-testid="surtax-row">
              <Field
                className={styles.county}
                label={t('surtaxEdit.county')}
                validationMessage={showErr(e.county)}
                validationState={showErr(e.county) ? 'error' : 'none'}
              >
                <Input
                  value={r.county}
                  placeholder={t('surtaxEdit.countyPlaceholder')}
                  onChange={(_ev, d) => updateRow(r.key, { county: d.value })}
                  disabled={pending}
                />
              </Field>
              <Field
                className={styles.rate}
                label={t('surtaxEdit.rateOverride')}
                hint={rows.length === 1 ? t('surtaxEdit.rateOverrideHint') : undefined}
                validationMessage={showErr(e.rate)}
                validationState={showErr(e.rate) ? 'error' : 'none'}
              >
                <Input
                  inputMode="decimal"
                  value={r.ratePercent}
                  placeholder={t('surtaxEdit.rateOverridePlaceholder')}
                  onChange={(_ev, d) => updateRow(r.key, { ratePercent: d.value })}
                  disabled={pending}
                />
              </Field>
              {multi && (
                <Field
                  className={styles.share}
                  label={t('surtaxEdit.share')}
                  validationMessage={showErr(e.share)}
                  validationState={showErr(e.share) ? 'error' : 'none'}
                >
                  <Input
                    inputMode="decimal"
                    value={r.sharePercent}
                    onChange={(_ev, d) => updateRow(r.key, { sharePercent: d.value })}
                    disabled={pending}
                  />
                </Field>
              )}
              {multi && (
                <Button
                  className={styles.removeRow}
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  aria-label={t('surtaxEdit.removeRow')}
                  onClick={() => removeRow(r.key)}
                  disabled={pending}
                />
              )}
            </div>
          );
        })}

        {!clearing && (
          <div>
            <Button appearance="secondary" icon={<AddRegular />} onClick={addCounty} disabled={pending}>
              {t('surtaxEdit.addCounty')}
            </Button>
          </div>
        )}

        {!clearing && attempted && validation.sumError && (
          <Text role="alert" style={{ color: tokens.colorPaletteRedForeground1 }}>
            {t('surtaxEdit.validation.sumNot100', { total: validation.sumError.totalPercent })}
          </Text>
        )}

        {(clearing || path === 'allocation') && (
          <div className={styles.month}>
            <Field
              label={t('surtaxEdit.effectiveFrom')}
              validationState={attempted && !monthOk ? 'error' : 'none'}
            >
              <Input
                type="month"
                value={effectiveMonth}
                onChange={(_ev, d) => setEffectiveMonth(d.value)}
                disabled={pending}
              />
            </Field>
            <Button
              appearance="subtle"
              onClick={() => setEffectiveMonth(`${yearInPinnedZone(now)}-01`)}
              disabled={pending}
            >
              {t('surtaxEdit.fromJanuary', { year: yearInPinnedZone(now) })}
            </Button>
          </div>
        )}

        {hasExistingSplit && (
          <div>
            <Button appearance="subtle" onClick={() => setClearing((c) => !c)} disabled={pending}>
              {clearing ? t('surtaxEdit.keepSplit') : t('surtaxEdit.clearSplit')}
            </Button>
          </div>
        )}

        {submitError && (
          <MessageBar intent="error">
            <MessageBarBody>{submitError}</MessageBarBody>
          </MessageBar>
        )}
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, d) => {
        if (!pending) onOpenChange(d.open);
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {t('surtaxEdit.title')}
            <Text block size={300} style={{ color: tokens.colorNeutralForeground3, fontWeight: 'normal' }}>
              {t('surtaxEdit.subtitle', { obligation: obligationLabel })}
            </Text>
          </DialogTitle>
          <DialogContent>{saved ? renderSaved(saved) : renderForm()}</DialogContent>
          <DialogActions>
            {saved ? (
              <Button appearance="primary" onClick={() => onOpenChange(false)}>
                {t('surtaxEdit.close')}
              </Button>
            ) : (
              <>
                <Button appearance="secondary" disabled={pending} onClick={() => onOpenChange(false)}>
                  {t('surtaxEdit.cancel')}
                </Button>
                <Button
                  appearance="primary"
                  disabled={pending || !seeded || !!loadError}
                  icon={pending ? <Spinner size="tiny" /> : undefined}
                  onClick={handleSave}
                >
                  {clearing ? t('surtaxEdit.remove') : t('surtaxEdit.save')}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
